import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ToolCall } from './types.js';

interface ToolCallDisplayProps {
  /** ツール呼び出し情報 */
  toolCall: ToolCall;
}

// ツール名の表示名マッピング
/** @graph-connects none */
const TOOL_DISPLAY_NAMES: Record<string, string> = {
  execute_sql: 'SQL実行',
  get_table_schema: 'スキーマ取得',
  list_tables: 'テーブル一覧',
  get_kpi_summary: 'KPIサマリー',
  compare_periods: '期間比較',
  find_anomalies: '異常検知',
  query_release_detail: 'リリース詳細',
  query_bug_detail: 'バグ詳細',
  query_develop_point_detail: '開発ポイント詳細',
  find_unmapped_dev_leaders: '未マッピング担当者',
  check_data_quality: 'データ品質チェック',
  get_trend_data: 'トレンドデータ',
  get_breakdown: '内訳分析',
};

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business ツールコールの実行状態（pending/running/success/error）に応じたアイコンSVGを返却
 * @graph-connects none
 */
function StatusIcon({ status }: { status: ToolCall['status'] }) {
  switch (status) {
    case 'pending':
    case 'running':
      return (
        <svg
          className="animate-spin w-4 h-4 text-[var(--color-accent-primary)]"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      );
    case 'success':
      return (
        <svg
          className="w-4 h-4 text-[var(--color-success)]"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'error':
      return (
        <svg
          className="w-4 h-4 text-[var(--color-error)]"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
            clipRule="evenodd"
          />
        </svg>
      );
  }
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ツールコール表示。AIが実行したツール（SQL実行・異常検知等）の実行状態・パラメータ・結果サマリーを折りたたみで表示
 * @graph-connects none
 */
export function ToolCallDisplay({ toolCall }: ToolCallDisplayProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const displayName = TOOL_DISPLAY_NAMES[toolCall.name] ?? toolCall.name;
  const hasParams = Object.keys(toolCall.params).length > 0;

  return (
    <div className="my-2">
      <button
        type="button"
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
        className="
          w-full flex items-center gap-2
          px-3 py-2
          bg-[var(--color-bg-surface)]
          border border-[var(--color-border)]
          rounded-lg
          text-left text-sm
          transition-all duration-[var(--transition-fast)]
          hover:border-[var(--color-border-hover)]
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus)]
        "
        aria-expanded={isExpanded}
        aria-label={t('chat.toolCall', 'ツール呼び出し: {{name}}', { name: displayName })}
      >
        <StatusIcon status={toolCall.status} />
        <span className="flex-1 font-medium text-[var(--color-text-primary)]">{displayName}</span>
        {toolCall.summary && (
          <span className="text-xs text-[var(--color-text-muted)] truncate max-w-[200px]">
            {toolCall.summary}
          </span>
        )}
        <svg
          className={`w-4 h-4 text-[var(--color-text-muted)] transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isExpanded && (
        <div className="mt-2 px-3 py-2 bg-[var(--color-bg-base)] border border-[var(--color-border)] rounded-lg">
          {hasParams && (
            <div className="mb-2">
              <span className="text-xs font-medium text-[var(--color-text-secondary)]">
                {t('chat.parameters', 'パラメータ')}:
              </span>
              <pre className="mt-1 text-xs text-[var(--color-text-muted)] overflow-x-auto">
                {JSON.stringify(toolCall.params, null, 2)}
              </pre>
            </div>
          )}
          {toolCall.summary && (
            <div>
              <span className="text-xs font-medium text-[var(--color-text-secondary)]">
                {t('chat.result', '結果')}:
              </span>
              <p className="mt-1 text-xs text-[var(--color-text-primary)]">{toolCall.summary}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
